import { LIBRARY_COLLECTION_DETAIL } from '@/constant/QueryKeyName';
import { DEFAULT_AVT_IMG } from '@/constant/imgImport';
import useToast from '@/hooks/useToast';
import useToggle from '@/hooks/useToggle';
import {
  ECollectionItemType,
  ICollection,
} from '@/interfaces/doccen-lib/collection.interface';
import { deleteDoccenCollectionDocument } from 'apis/tms/library/collection.api';
import { PATH_NAME } from 'configs/pathName';
import { Avatar } from 'design-system/components/Avatar';
import { Button } from 'design-system/components/Button';
import { Divider } from 'design-system/components/Divider';
import { Icon } from 'design-system/components/Icon';
import { Tooltip } from 'design-system/components/Tooltips';
import { Typography } from 'design-system/components/Typography';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';

const Popover = dynamic(() => import('@mui/material/Popover'));
const DropdownRow = dynamic(
  () => import('design-system/components/Dropdown/DropdownRow')
);
const ModalUpsertExternalDocument = dynamic(
  () => import('./modal/ModalUpsertExternalDocument')
);

type DocumentItemProps = {
  viewMode?: boolean;
} & ICollection['items'][number];

const DocumentItem = (props: DocumentItemProps) => {
  const {
    viewMode,
    id,
    itemId,
    type,
    name: title,
    link,
    owner: {
      name,
      academicRank,
      avatar: ownerAvatar,
      profileUrl: ownerProfileUrl,
    },
  } = props;
  const router = useRouter();

  const ownerName = `${academicRank ? `${academicRank}. ` : ''}${name}`;

  const { cid: collectionId } = router.query;
  const toast = useToast();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: deleteDoccenCollectionDocument,
    onSuccess: (data) => {
      handleClose();
      if (collectionId !== data.collectionId) {
        router.replace({
          pathname: PATH_NAME.LIBRARY_COLLECTION_DETAIL,
          query: { cid: data.collectionId },
        });
      } else {
        queryClient.refetchQueries([
          LIBRARY_COLLECTION_DETAIL,
          data.collectionId,
        ]);
      }
    },
    onError: () => {
      toast('Đã xảy ra lỗi', 'error');
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <div className="flex items-center gap-2 rounded-lg border border-Primary/Other(Border) bg-white p-2">
        <Icon iconName="attachment" />
        <Link
          passHref
          href={
            type === ECollectionItemType.ContentLink
              ? link!
              : {
                  pathname: PATH_NAME.LIBRARY_DOCUMENT_DETAIL,
                  query: {
                    did: itemId,
                  },
                }
          }
        >
          <a className="w-full flex-1">
            <Tooltip title={title}>
              <Typography variant="body3" className="line-clamp-1 w-fit">
                {title}
              </Typography>
            </Tooltip>
          </a>
        </Link>
        <Divider type="vertical" className="!h-auto" />
        <Link href={ownerProfileUrl} passHref>
          <a
            className={`hidden max-w-[80px] flex-1 items-center gap-2 lg:flex xl:max-w-[240px]`}
          >
            {type === ECollectionItemType.Material && (
              <Avatar
                type="image"
                src={ownerAvatar || DEFAULT_AVT_IMG}
                size={24}
                className="cursor-pointer"
              />
            )}
            <Typography
              variant="link14-regular-underline"
              className="line-clamp-1 cursor-pointer text-Primary/Main"
            >
              {ownerName}
            </Typography>
          </a>
        </Link>
        {!viewMode && (
          <Button
            mode="icon-only"
            size="small"
            icon={{
              name: 'more',
            }}
            onClick={handleClick}
          />
        )}
      </div>

      {open && !viewMode && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              className:
                '!rounded-[10px] !shadow-elevator2 space-y-1 py-2 min-w-[176px]',
            },
          }}
        >
          {type === ECollectionItemType.ContentLink && (
            <EditContentLinkButton
              collectionId={collectionId as string}
              content={props}
            />
          )}
          <DropdownRow
            onClick={() => {
              mutate({
                collectionId: collectionId as string,
                itemId: id,
              });
            }}
          >
            <Typography variant="display3">Xóa nội dung</Typography>
          </DropdownRow>
        </Popover>
      )}
    </>
  );
};

export default DocumentItem;

const EditContentLinkButton = ({
  collectionId,
  content,
}: {
  collectionId: string;
  content: DocumentItemProps;
}) => {
  const [openEditContentLinkModal, toggleEditContentLinkModal] = useToggle();
  return (
    <>
      <DropdownRow onClick={toggleEditContentLinkModal}>
        <Typography variant="display3">Chỉnh sửa</Typography>
      </DropdownRow>
      {openEditContentLinkModal && (
        <ModalUpsertExternalDocument
          open={openEditContentLinkModal}
          onClose={toggleEditContentLinkModal}
          collectionId={collectionId}
          defaultData={{
            name: content.name,
            url: content.link || '',
            authorName: content.owner.name,
            authorUrl: content.owner.profileUrl,
            id: content.id,
            itemId: content.itemId,
          }}
        />
      )}
    </>
  );
};
